'use client';

import { c as _c } from "react/compiler-runtime";
import type { CodeSkillInventory, CodeSkillMetadata } from '@/lib/code-sessions/skills';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { CodeSessionRow } from './types';
import { isRuntimeReady } from './runtime/capabilities';
import { runtimeFor } from './runtime/runtime-for';
export type CodeSkillSource = {
  status: 'not-ready' | 'loading' | 'error';
} | {
  status: 'ready';
  skills: CodeSkillMetadata[];
};
export function codeSessionSkillsOptions(sessionId: string) {
  return {
    queryKey: ['code-session-skills', sessionId] as const,
    queryFn: ({
      signal
    }: {
      signal: AbortSignal;
    }): Promise<CodeSkillInventory> => runtimeFor(sessionId).skills({
      signal
    }),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: undefined
  };
}
export function useCodeSessionSkills(session, resuming, readyRevision) {
  const $ = _c(24);
  let t0;
  if ($[0] !== resuming || $[1] !== session) {
    t0 = session?.openCodeSessionId && isRuntimeReady({
      session,
      resuming
    }) && ["idle", "running"].includes(session.status);
    $[0] = resuming;
    $[1] = session;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const enabled = Boolean(t0);
  const t1 = session?.id ?? "";
  let t2;
  if ($[3] !== t1) {
    t2 = codeSessionSkillsOptions(t1);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== enabled || $[6] !== t2) {
    t3 = {
      ...t2,
      enabled
    };
    $[5] = enabled;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const query = useQuery(t3);
  const t4 = session?.id;
  let t5;
  if ($[8] !== readyRevision || $[9] !== t4) {
    t5 = {
      sessionId: t4,
      readyRevision
    };
    $[8] = readyRevision;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const previous = useRef(t5);
  let t6;
  if ($[11] !== enabled || $[12] !== query || $[13] !== readyRevision || $[14] !== session?.id) {
    t6 = () => {
      if (enabled && previous.current.sessionId === session?.id && previous.current.readyRevision !== readyRevision) {
        query.refetch();
      }
      previous.current = {
        sessionId: session?.id,
        readyRevision
      };
    };
    $[11] = enabled;
    $[12] = query;
    $[13] = readyRevision;
    $[14] = session?.id;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = session?.id;
  let t8;
  if ($[16] !== enabled || $[17] !== query.refetch || $[18] !== readyRevision || $[19] !== t7) {
    t8 = [enabled, query.refetch, readyRevision, t7];
    $[16] = enabled;
    $[17] = query.refetch;
    $[18] = readyRevision;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useEffect(t6, t8);
  if (!enabled) {
    let t9;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        status: "not-ready"
      };
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    return t9;
  }
  if (query.isPending) {
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        status: "loading"
      };
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  if (query.isError) {
    let t9;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        status: "error"
      };
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    return t9;
  }
  return query.data;
}